import { RideBar } from "../components/RideBar";
import { reqToJoinApi, rideRequestsApi, searchRidesApi } from "services/auth/rideApis";
import { addRecurringRequestApi } from "services/auth/recurringApis";
import { useSearchParams, useNavigate } from "react-router-dom";
import { addInterestApi } from "services/auth/interestApis";
import { FaBell } from "react-icons/fa6";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";

export const RideDetails = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [rides, setRides] = useState(null);
  const [loading, setLoading] = useState(true);
  const [existingRequests, setExistingRequests] = useState(new Set());
  const [requestInProgress, setRequestInProgress] = useState(new Set());

  const from = searchParams?.get("from");
  const to = searchParams?.get("to");
  const date = searchParams?.get("date");
  const femaleOnly = searchParams?.get("femaleOnly");

  // Check if a location value is valid (contains alphanumeric content)
  const isValidLocation = (value) => {
    if (!value || value.trim().length === 0) return true;
    return /[a-zA-Z0-9]{2,}/.test(value);
  };
  const isSameLocation = Boolean(
    from && to && from.trim().length > 0 && to.trim().length > 0 && from.trim().toLowerCase() === to.trim().toLowerCase()
  );
  const hasInvalidLocations = (from && !isValidLocation(from)) || (to && !isValidLocation(to));

  // Fetch rides based on search params
  useEffect(() => {
    const fetchRides = async () => {
      if (hasInvalidLocations || isSameLocation) {
        setRides([]);
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const data = await searchRidesApi(from || "", to || "", date || "", femaleOnly || "false");
        setRides(data);
      } catch (error) {
        console.error("Failed to fetch rides:", error);
        toast.error("Failed to load rides");
        setRides([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRides();
  }, [from, to, date, femaleOnly]);

  // Fetch existing requests
  useEffect(() => {
    const fetchExistingRequests = async () => {
      try {
        const requests = await rideRequestsApi();
        const requestedRideIds = new Set(requests.map((req) => req.rideId));
        setExistingRequests(requestedRideIds);
      } catch (error) {
        console.error("Failed to fetch existing requests:", error);
      }
    };
    fetchExistingRequests();
  }, []);

  const handleJoinReq = async (id) => {
    try {
      setRequestInProgress((prev) => new Set([...prev, id]));

      const response = await reqToJoinApi(id);
      if (response) {
        // Update existingRequests to reflect the new request
        setExistingRequests((prev) => new Set([...prev, id]));
      }
    } catch (error) {
      console.error("Failed to send join request:", error);
      toast.error("Failed to send request");
    } finally {
      setRequestInProgress((prev) => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
  };

  const handleRecurringJoinReq = async (rideId, recurrenceGroupId) => {
    try {
      setRequestInProgress((prev) => new Set([...prev, "grp_"+recurrenceGroupId]));
      await addRecurringRequestApi(recurrenceGroupId, rideId);
    } finally {
      setRequestInProgress((prev) => {
        const newSet = new Set(prev);
        newSet.delete("grp_"+recurrenceGroupId);
        return newSet;
      });
    }
  };

  const registerInterest = async () => {
    await addInterestApi(from, to, date ? date : null);
  };

  if (loading) {
    return (
      <div className="max-sm:backdrop-blur-sm overflow-x-hidden hideScrollBars flex-1 flex justify-center items-center py-4 px-2 md:px-6 backdrop-blur z-10 w-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500 mx-auto"></div>
          <p className="mt-4 text-zinc-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-sm:backdrop-blur-sm overflow-x-hidden hideScrollBars flex-1 flex justify-center py-4 px-2 md:px-6 backdrop-blur z-10 w-full">
      {rides?.length > 0 ? (
        <div className="space-y-4 h-fit flex flex-col items-center w-full">
          {rides?.map((ride, i) => {
            const isRequested = existingRequests.has(ride.id);
            const isInProgress = requestInProgress.has(ride.id);

            return (
              <div
                className="w-full max-w-[95vw] md:max-w-3xl"
                key={ride.id || i}
              >
                <RideBar
                  ride={ride}
                  action={
                    <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
                      <Button
                        className="py-2 px-5 rounded-xl text-sm font-semibold bg-amber-500 hover:bg-amber-600 text-zinc-950 transition-colors shadow-xs disabled:opacity-50 w-full sm:w-auto"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleJoinReq(ride.id);
                        }}
                        disabled={isRequested || isInProgress}
                      >
                        {isRequested
                          ? "Request Sent"
                          : isInProgress
                            ? "Sending..."
                            : "Request to join"}
                      </Button>
                      {ride.recurrenceGroupId && (
                        <Button
                          className="py-2 px-5 rounded-xl text-sm font-semibold bg-zinc-900 hover:bg-zinc-800 text-white transition-colors shadow-xs disabled:opacity-50 w-full sm:w-auto"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRecurringJoinReq(ride.id, ride.recurrenceGroupId);
                          }}
                          disabled={requestInProgress.has("grp_" + ride.recurrenceGroupId)}
                        >
                          {requestInProgress.has("grp_" + ride.recurrenceGroupId) ? "Sending..." : "Request Recurring Route"}
                        </Button>
                      )}
                    </div>
                  }
                />
              </div>
            );
          })}
        </div>
      ) : (
        <div className="items-center justify-center flex flex-col h-full">
          <div className="items-center p-8 rounded-lg text-center justify-center flex flex-col bg-white/80">
            {isSameLocation ? (
              <>
                <span className="text-3xl font-semibold text-red-600">
                  Pickup and drop-off cannot be the same.
                </span>
                <span className="text-lg text-zinc-500 mt-4">
                  Please choose different locations for pickup and drop-off.
                </span>
                <Button
                  className="mt-6 bg-amber-500 hover:bg-amber-600 text-white font-semibold px-6 py-2 rounded-xl"
                  onClick={() => navigate("/search")}
                >
                  Modify Search
                </Button>
              </>
            ) : hasInvalidLocations ? (
              <>
                <span className="text-3xl font-semibold text-red-600">
                  Invalid source or destination.
                </span>
                <span className="text-lg text-zinc-500 mt-4">
                  Please enter valid location names (not special characters or emojis).
                </span>
              </>
            ) : (
              <>
                <span className="text-3xl font-semibold">
                  No matching rides found for your search criteria.
                </span>
                <p className="text-sm text-zinc-500 mt-2">
                  Rides that have already departed are not shown.
                </p>
              </>
            )}
            {!isSameLocation && from && to && from.length > 0 && to.length > 0 && (
              <>
                <span className="text-xl text-zinc-500 mt-4 mb-2">
                  Receive an email notification when a matching ride becomes
                  available?
                </span>
                <button
                  onClick={registerInterest}
                  className="p-2 rounded-lg bg-amber-500 text-white drop-shadow-md font-semibold inline-flex justify-center items-center gap-1"
                >
                  <FaBell /> Notify Me
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};